import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { API_URL } from "../../config";
import StripeAchSetup from "./StripeAchSetup";
import StripeCardSetup from "./StripeCardSetup";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

function dollars(cents) {
  const d = (cents ?? 0) / 100;
  return `$${Number.isInteger(d) ? d : d.toFixed(2)}`;
}

function methodLabel(info) {
  const kind = info.payment_method_type === "ach" ? "Bank" : "Card";
  const name = info.payment_method_bank_name;
  return `${name || kind} •• ${info.payment_method_last4 || "····"}`;
}

/**
 * The standing payment link (CIN-1203) and the membership opt-out page.
 *
 * Serves three states, because a free first booking deliberately leaves the
 * carrier a non-member: `inactive` (no membership yet — this is the link ops
 * trains them on, so adding a method is the whole point), `active` (manage or
 * replace the method, or cancel), and `opted_out`.
 */
export default function SubscriptionManage() {
  const { token } = useParams();
  // loading | view | method_chooser | ach_setup | card_setup | canceled | error
  const [screen, setScreen] = useState("loading");
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [setupSession, setSetupSession] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const baseUrl = useMemo(
    () => `${API_URL}/book-it/subscription/${token}`,
    [token],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(baseUrl);
        if (cancelled) return;
        setInfo(data);
        setScreen(
          data.subscription_status === "opted_out" ? "canceled" : "view",
        );
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(
          err?.response?.data?.detail ||
            "We couldn't open this link. It may have expired.",
        );
        setScreen("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  async function handleCancel() {
    setSubmitting(true);
    setErrorMessage("");
    try {
      const { data } = await axios.post(`${baseUrl}/opt-out`);
      setResult(data);
      setScreen("canceled");
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  // force_fresh: they came here to add a method, so never short-circuit to the
  // one already on file — replacing it is the other reason to be on this page.
  async function handleAddMethod(methodType) {
    setErrorMessage("");
    try {
      const { data } = await axios.post(`${baseUrl}/setup`, {
        method_type: methodType,
        force_fresh: true,
      });
      setSetupSession({ ...data, method_type: methodType });
      setScreen(methodType === "ach" ? "ach_setup" : "card_setup");
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.detail ||
          "We couldn't start payment setup. Please try again.",
      );
    }
  }

  function handleSetupSucceeded() {
    setInfo((prev) => (prev ? { ...prev, has_payment_method: true } : prev));
    setScreen("view");
  }

  const card = "w-full max-w-[480px] bg-white rounded-2xl shadow-md p-7";
  // Membership switched off server-side = carriers are free; booking fee only.
  const membershipOn = Boolean(info?.subscription_enabled);
  const isMember = membershipOn && info?.subscription_status === "active";
  const keptFor = membershipOn
    ? "booking fees and your weekly membership"
    : "booking fees";

  return (
    <div className="min-h-screen flex flex-col items-center bg-slate-50 text-slate-900 antialiased px-4 py-8">
      <div className="flex items-center gap-2 mb-5">
        <img src="/cinesis-logo.png" alt="Cinesis" className="h-8 w-auto" />
        <span className="text-base font-bold tracking-tight text-slate-900">
          Cinesis
        </span>
      </div>

      {screen === "loading" && (
        <div className={`${card} text-center text-slate-600`}>Loading…</div>
      )}

      {screen === "error" && (
        <div className={`${card} text-center text-red-700`}>{errorMessage}</div>
      )}

      {screen === "view" && info && (
        <div className={card}>
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 mb-1.5">
            For {info.trucker_name || "you"}
          </p>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900 mb-4">
            {isMember ? "Your Cinesis membership" : "Your Cinesis payments"}
          </h1>

          {isMember ? (
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl px-4 py-3.5 text-[13px] leading-relaxed text-indigo-900 mb-5">
              <strong className="block text-[15px] font-semibold text-indigo-900 mb-1">
                {dollars(info.weekly_amount_cents)}/week · active
              </strong>
              You're billed once a month for ongoing load suggestions — your
              weeks are added up and charged via ACH. You can cancel anytime
              below; we'll settle only the whole weeks accrued so far, then
              stop.
            </div>
          ) : (
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl px-4 py-3.5 text-[13px] leading-relaxed text-slate-700 mb-5">
              <strong className="block text-[15px] font-semibold text-slate-900 mb-1">
                No payment method on file
              </strong>
              Add one and we'll keep sending you loads — booking is one tap, and
              we handle the broker.
              {membershipOn && (
                <>
                  {" "}
                  Once you add one, ongoing load suggestions are{" "}
                  {dollars(info.weekly_amount_cents)}/week, billed monthly, and
                  only for weeks we actually send you something. Cancel anytime.
                </>
              )}
            </div>
          )}

          {info.has_payment_method && (
            <div className="bg-slate-50 rounded-xl px-4 py-3.5 mb-5 text-sm font-semibold text-slate-900">
              {methodLabel(info)}
            </div>
          )}

          {errorMessage && (
            <div className="text-[12px] text-red-700 mb-3">{errorMessage}</div>
          )}

          <button
            type="button"
            onClick={() => setScreen("method_chooser")}
            className="w-full !shadow-none !bg-indigo-600 hover:!bg-indigo-700 !text-white !border-0 !rounded-xl !px-4 !py-3.5 !text-[15px] !font-semibold mb-3"
          >
            {info.has_payment_method
              ? "Change payment method"
              : "Add a payment method"}
          </button>

          {isMember && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className="w-full !shadow-none !bg-white hover:!bg-red-50 disabled:!opacity-60 !text-red-700 !border !border-red-200 !rounded-xl !px-4 !py-3 !text-[14px] !font-semibold"
            >
              {submitting ? "Canceling…" : "Cancel membership"}
            </button>
          )}
        </div>
      )}

      {screen === "method_chooser" && (
        <div className={card}>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900 mb-1.5">
            How do you want to pay?
          </h1>
          <p className="text-sm text-slate-500 mb-4">
            Nothing is charged today. We use this for {keptFor}.
          </p>
          {errorMessage && (
            <div className="text-[12px] text-red-700 mb-3">{errorMessage}</div>
          )}
          <button
            type="button"
            onClick={() => handleAddMethod("ach")}
            className="!shadow-none flex flex-col items-start w-full !p-4.5 mb-3 !rounded-xl !border !border-slate-200 hover:!border-indigo-500 hover:!bg-indigo-50/40 !bg-white !text-left active:scale-[0.99] transition"
          >
            <span className="text-base font-semibold text-slate-900">
              Pay with bank (ACH)
            </span>
            <span className="text-[13px] text-slate-500">
              Lower fees, takes about a minute
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleAddMethod("card")}
            className="!shadow-none flex flex-col items-start w-full !p-4.5 mb-3 !rounded-xl !border !border-slate-200 hover:!border-indigo-500 hover:!bg-indigo-50/40 !bg-white !text-left active:scale-[0.99] transition"
          >
            <span className="text-base font-semibold text-slate-900">
              Pay with card
            </span>
            <span className="text-[13px] text-slate-500">Instant setup</span>
          </button>
          <button
            type="button"
            onClick={() => setScreen("view")}
            className="w-full !shadow-none !bg-transparent !text-slate-500 !border-0 !text-[13px] !font-medium"
          >
            Back
          </button>
        </div>
      )}

      {(screen === "ach_setup" || screen === "card_setup") && !stripePromise && (
        <div className={`${card} text-center text-red-700`}>
          Stripe is not configured. Please contact support.
        </div>
      )}

      {(screen === "ach_setup" || screen === "card_setup") && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: setupSession.client_secret }}
        >
          {screen === "ach_setup" ? (
            <StripeAchSetup
              setupSession={setupSession}
              truckerName={info?.trucker_name}
              onSucceeded={handleSetupSucceeded}
            />
          ) : (
            <StripeCardSetup
              setupSession={setupSession}
              subtitle={`Nothing is charged today. We keep it on file for ${keptFor}.`}
              onSucceeded={handleSetupSucceeded}
            />
          )}
        </Elements>
      )}

      {screen === "canceled" && (
        <div className={`${card} text-center`}>
          <div className="text-3xl mb-3">✓</div>
          <h1 className="text-[20px] font-bold tracking-tight text-slate-900 mb-2">
            Membership canceled
          </h1>
          <p className="text-sm text-slate-600">
            {result && result.weeks > 0
              ? `A final charge of ${dollars(
                  result.final_amount_cents,
                )} for ${result.weeks} week${
                  result.weeks === 1 ? "" : "s"
                } is on its way. No further charges.`
              : "No further charges. Thanks for riding with Cinesis."}
          </p>
        </div>
      )}

      <div className="mt-6 text-xs text-slate-400 text-center">
        Powered by Cinesis · Secure payments by Stripe
      </div>
    </div>
  );
}
